import { MINIMUM_SEARCH_CHARACTERS } from 'config/constants/info'
import { gql } from 'graphql-request'
import { useEffect, useState } from 'react'
import { usePoolDatasQuery, useTokenDatasQuery } from 'state/info/hooks'
import { PoolData, TokenData } from 'state/info/types'
import { infoClientWithChain } from 'utils/graphql'

const NATIVE_TOKENS = {
  "PLS": "WPLS",
  "ETH": "WETH",
  "BNB": "WBNB",
}

// orderBy: symbol causes the correct index to be selected
// if removed it will orderBy: id and take 300x longer to query.
const TOKEN_SEARCH = gql`
  query tokens($symbol: String, $name: String, $id: String) {
      asSymbol: tokens(
          first: 10
          where: { 
              symbol_contains_nocase: $symbol
          }
      ) {
          id
      }
      asName: tokens(
          first: 10
          where: { 
              name_contains_nocase: $name
          }
      ) {
          id
      }
      asExactSymbol: tokens(
          where: {
              symbol: $symbol
          }
          orderBy: symbol
      ) {
          id
      }
      asAddress: tokens(first: 1, where: { id: $id }) {
          id
      }
  }
`

const POOL_SEARCH = gql`
  query pools($tokens: [Bytes]!, $id: String) {
    as0: pairs(first: 10, where: { token0_in: $tokens }) {
        id
    }
    as1: pairs(first: 10, where: { token1_in: $tokens }) {
        id
    }
    asAddress: pairs(first: 1, where: { id: $id }) {
        id
    }
  }
`

interface SingleQueryResponse {
  id: string
}

interface TokenSearchResponse {
  asSymbol: SingleQueryResponse[]
  asName: SingleQueryResponse[]
  asExactSymbol: SingleQueryResponse[]
  asAddress: SingleQueryResponse[]
}
interface PoolSearchResponse {
  as0: SingleQueryResponse[]
  as1: SingleQueryResponse[]
  asAddress: SingleQueryResponse[]
}

const getIds = (entityArrays: SingleQueryResponse[][]) => {
  const ids = entityArrays.flat().map((entity) => entity.id)
  return Array.from(new Set(ids))
}

const useFetchSearchResults = (
  chainId: number,
  protocol: string,
  searchString: string,
): {
  tokens: TokenData[] | undefined
  pools: (PoolData | undefined)[]
  tokensLoading: boolean
  poolsLoading: boolean
  error: boolean
} => {
  const [searchResults, setSearchResults] = useState({
    tokens: [] as string[], // Token ids found by search query
    pools: [] as string[], // Pool ids found by search query
    loading: false, // Search query is in progress
    error: false, // GraphQL returned error
  })

  const searchStringTooShort = searchString.length < MINIMUM_SEARCH_CHARACTERS

  // New value received, reset state
  useEffect(() => {
    setSearchResults({
      tokens: [],
      pools: [],
      loading: !searchStringTooShort,
      error: false,
    })
  }, [searchString, searchStringTooShort])

  const tokenQuery = TOKEN_SEARCH
  const poolQuery = POOL_SEARCH
  useEffect(() => {
    const search = async () => {
      try {
        const tokens = await infoClientWithChain(chainId, protocol).request<TokenSearchResponse>(tokenQuery, {
          symbol: NATIVE_TOKENS[searchString.toUpperCase()] ? NATIVE_TOKENS[searchString.toUpperCase()] : searchString.toUpperCase(),
          // Most well known tokens have first letter capitalized
          name: searchString.charAt(0).toUpperCase() + searchString.slice(1),
          id: searchString.toLowerCase(),
        })

        const tokenIds = getIds([tokens.asAddress, tokens.asSymbol, tokens.asName, tokens.asExactSymbol]).map((d) => d.toLowerCase())

        const pools = await infoClientWithChain(chainId, protocol).request<PoolSearchResponse>(poolQuery, {
          tokens: tokenIds,
          id: searchString.toLowerCase(),
        })
        setSearchResults({
          tokens: tokenIds,
          pools: getIds([pools.asAddress, pools.as0, pools.as1]).map((d) => d.toLowerCase()),
          loading: false,
          error: false,
        })
      } catch (error) {
        console.error(`Search failed for ${searchString}`, error)
        setSearchResults({
          tokens: [],
          pools: [],
          loading: false,
          error: true,
        })
      }
    }
    if (!searchStringTooShort) {
      search()
    }
  }, [chainId, searchString, searchStringTooShort, protocol, poolQuery, tokenQuery])

  // Save ids to Redux
  // Token and Pool updater will then go fetch full data for these addresses
  // These hooks in turn will return data of tokens that have been fetched
  const tokenDatasFull = useTokenDatasQuery(protocol, searchResults.tokens, false)
  const poolDatasFull = usePoolDatasQuery(protocol, searchResults.pools)

  // If above hooks returned not all tokens/pools it means
  // that some requests for full data are in progress
  const tokensLoading = tokenDatasFull?.length !== searchResults.tokens.length || searchResults.loading
  const poolsLoading = poolDatasFull.length !== searchResults.pools.length || searchResults.loading

  return {
    tokens: tokenDatasFull,
    pools: poolDatasFull,
    tokensLoading,
    poolsLoading,
    error: searchResults.error,
  }
}

export default useFetchSearchResults
